import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowRight, Phone, MessageCircle, MapPin, Navigation, 
  Star, DollarSign, CheckCircle, X, Send
} from 'lucide-react';
import { useRide } from '@/context/RideContext';
import { rideService } from '@/services/rideService';
import type { Ride } from '@/types';

const DriverRide: React.FC = () => {
  const navigate = useNavigate();
  const { rideId } = useParams();
  const { 
    currentRide, messages, sendMessage, 
    startRide, completeRide, driverArrived 
  } = useRide();
  
  const [ride, setRide] = useState<Ride | null>(null);
  const [showChat, setShowChat] = useState(false);
  const [messageText, setMessageText] = useState('');

  useEffect(() => {
    if (rideId) {
      loadRide();
    }
  }, [rideId]);

  const loadRide = async () => {
    if (!rideId) return;
    try {
      const rideData = await rideService.getRide(rideId);
      if (rideData) {
        setRide(rideData);
      }
    } catch (error) {
      console.error('Error loading ride:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    await sendMessage(messageText);
    setMessageText('');
  };

  const handleArrived = async () => {
    await driverArrived();
  };

  const handleStart = async () => {
    await startRide();
  };

  const handleComplete = async () => {
    await completeRide();
    navigate('/driver/home');
  };

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      driver_assigned: 'متجه لنقطة الانطلاق',
      driver_arrived: 'في نقطة الانطلاق',
      in_progress: 'الرحلة جارية',
      completed: 'تم إكمال الرحلة',
    };
    return texts[status] || status;
  };

  const activeRide = currentRide || ride;

  if (!activeRide) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">الرحلة غير موجودة</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate('/driver/home')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <ArrowRight className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">تفاصيل الرحلة</h1>
            <p className="text-sm text-gray-500">#{activeRide.id.slice(-8).toUpperCase()}</p>
          </div>
        </div>
      </header>

      {/* Status Banner */}
      <div className="bg-secondary-500 text-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/70 text-sm">حالة الرحلة</p>
            <p className="font-bold text-lg">{getStatusText(activeRide.status)}</p>
          </div>
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
            <Navigation className="w-6 h-6 animate-pulse" />
          </div>
        </div>
      </div>

      {/* Map Placeholder */}
      <div className="h-48 bg-gray-200 flex items-center justify-center">
        <p className="text-gray-500">خريطة المسار</p>
      </div>

      {/* Passenger Info */}
      <div className="bg-white p-4 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center">
            <span className="text-xl font-bold text-primary-600">
              {activeRide.passengerId.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1">
            <p className="font-bold">الراكب</p>
            <p className="text-gray-500 text-sm">رحلة {activeRide.type}</p>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => setShowChat(true)}
              className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center"
            >
              <MessageCircle className="w-5 h-5 text-primary-600" />
            </button>
            <button className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Phone className="w-5 h-5 text-green-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Ride Details */}
      <div className="flex-1 p-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">نقطة الانطلاق</p>
                <p className="font-medium">{activeRide.pickup?.address}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">الوجهة</p>
                <p className="font-medium">{activeRide.destination?.address}</p>
              </div>
            </div>
          </div>

          <div className="border-t mt-4 pt-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-500">طريقة الدفع</span>
              <span className="font-medium">{activeRide.paymentMethod}</span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-gray-500">السعر المقدر</span>
              <span className="font-bold text-lg">{activeRide.estimatedPrice} ج.س</span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="p-4 space-y-3">
        {activeRide.status === 'driver_assigned' && (
          <button
            onClick={handleArrived}
            className="w-full bg-primary-500 text-white py-4 rounded-xl font-bold hover:bg-primary-600 transition-colors"
          >
            لقد وصلت
          </button>
        )}
        
        {activeRide.status === 'driver_arrived' && (
          <button
            onClick={handleStart}
            className="w-full bg-green-500 text-white py-4 rounded-xl font-bold hover:bg-green-600 transition-colors"
          >
            بدء الرحلة
          </button>
        )}
        
        {activeRide.status === 'in_progress' && (
          <button
            onClick={handleComplete}
            className="w-full bg-secondary-500 text-white py-4 rounded-xl font-bold hover:bg-secondary-600 transition-colors"
          >
            إكمال الرحلة
          </button>
        )}
      </div>

      {/* Chat Modal */}
      {showChat && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
          <div className="bg-white rounded-t-3xl w-full h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold">الدردشة مع الراكب</h3>
              <button onClick={() => setShowChat(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-auto p-4">
              {messages.length === 0 ? (
                <p className="text-center text-gray-500">لا توجد رسائل</p>
              ) : (
                messages.map((msg) => (
                  <div 
                    key={msg.id} 
                    className={`mb-4 ${msg.senderType === 'driver' ? 'text-left' : 'text-right'}`}
                  >
                    <div className={`inline-block px-4 py-2 rounded-2xl ${
                      msg.senderType === 'driver' 
                        ? 'bg-secondary-500 text-white' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {msg.content}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-4 border-t">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="اكتب رسالتك..."
                  className="flex-1 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-secondary-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                />
                <button
                  onClick={handleSendMessage}
                  className="w-12 h-12 bg-secondary-500 text-white rounded-xl flex items-center justify-center"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverRide;
